import React, { useState } from 'react';
import { Typography, Stack, Button, useTheme, Divider, Box, Grid, Tooltip } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import route from '../../../utils/Routes';
import BackButton from '../../../components/BackButton';
import { useAuthContext } from '../../../context/AuthContext';
import { useQuery, useQueryClient } from 'react-query';
import { isEmpty, isNotEmpty, makeTxtOverFLow } from '../../../utils/format';
import QueryKey from '../../../utils/QueryKey';
import { useEffect } from 'react';
import { customAlert, notify } from '../../../components/notify';
import ScrollBox from '../../../components/ScrollBox';
import BgBoxOld from '../../../components/BgBoxOld';
import ProdReqTimeline from '../../../components/ProdReqTimeline';
import constants from '../../../utils/Constants';
import CtrlLabelTxtFieldInfo from '../../../components/CtrlLabelTxtFieldInfo';
import { getSingleNewProdReqServ, sendMailNewProdReqServ, updateNewProdReqStatusServ } from '../../../services/newprodreq-service';
import { InsertDriveFile, Send } from '@mui/icons-material';
import NotesMdl from '../../../components/NotesMdl';
import MessagePopUp from '../../../components/MessagePopUp';
import LabelTxt from '../../../components/LabelTxt';



function NewProdReqInfoPage() {
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const location = useLocation()
    const newProdReqId = searchParams.get("newProdReqId");
    const { setLoadingScreen, ctxtUser, setNavComValue } = useAuthContext();
    const { isLoading, isError, error, data: reqData } = useQuery([QueryKey.singleNewProdReq, newProdReqId],
        () => getSingleNewProdReqServ(newProdReqId),
        {
            enabled: isNotEmpty(newProdReqId),
            refetchOnMount: true,
            refetchOnWindowFocus: false,
        });
    const txtFieldSx = { width: "100%", height: 30, borderRadius: 1 }
    const [access, setAccess] = useState({ canEdit: false, canApprove: false, canSend: false })
    const [payload, setPayload] = useState(null)
    const [notesMdl, setNotesMdl] = useState(false);
    const handleNotesMdlClose = () => setNotesMdl(false);
    const [poMsgOpen, setPoMsgOpen] = useState(false);
    const handlePoMsgMdlOpen = () => setPoMsgOpen(true);
    const handlePoMsgMdlClose = () => setPoMsgOpen(false);
    const dialogMessage = {
        title: "NEw Product Request Forwarded Successfully",
        description: "Your New product request has been successfully forwarded to AMTC Team",
        buttonText: "Continue"
    };
    const namestyle = { fontSize: 12, fontWeight: 500, display: "inline-block", width: 120 }
    const detailstyle = { fontSize: 12, fontWeight: 400, display: "inline-block" }

    useEffect(() => {
        setLoadingScreen(isLoading);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    useEffect(() => {
        if (!reqData)
            return
        const canEdit = getEditAccess(reqData, ctxtUser?.userType)
        const canApprove = getApproveAccess(reqData, ctxtUser?.userType)
        const canSend = getSendMailAccess(reqData, ctxtUser?.userType)
        setAccess({ canEdit, canApprove, canSend })
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [reqData]);

    useEffect(() => {
        if (ctxtUser?.isComUser === false) {
            setNavComValue({ comId: reqData?.comId });
            return () => setNavComValue({ comId: "" }); // Reset when leaving the page
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, reqData?.comId, ctxtUser]);

    function getEditAccess(data, userType) {
        const { status, statusLevel } = data?.currStatusObj || {};
        const { requestor, approver, line_manager, focal_point } = data;
        const cs = { ...constants }

        if (['mailsend'].includes(status))
            return false
        if (userType === cs.requestor) {
            if (isNotEmpty(requestor) && isEmpty(approver) && isEmpty(line_manager) & isEmpty(focal_point))
                return true;
            return false;
        }
        if (userType === cs.approver) {
            if (isNotEmpty(line_manager) || isNotEmpty(focal_point))
                return false
            if (['approved', 'held'].includes(status))
                return false
            return true;
        }
        if (userType === cs.lineManager) {
            if (statusLevel === cs.approver) {
                if (isEmpty(requestor))
                    return true
                if (['approved'].includes(status))
                    return true
            }
            if (statusLevel === cs.lineManager) {
                if (['edited', 'created'].includes(status))
                    return true
            }
            if (statusLevel === cs.focalPoint) {
                if (isEmpty(approver))
                    return true;
            }
            return false
        }
        if (userType === cs.focalPoint) {
            if (statusLevel === cs.approver) {
                if (isEmpty(requestor))
                    return true
                if (['approved'].includes(status))
                    return true
            }
            if (statusLevel === cs.lineManager) {
                return false
            }
            if (statusLevel === cs.focalPoint) {
                if (isEmpty(approver))
                    return true
            }
            return false
        }
    }

    function getApproveAccess(data, userType) {
        const { status, statusLevel } = data?.currStatusObj || {};
        const { requestor, approver, line_manager, focal_point } = data;
        const cs = { ...constants }

        if (['mailsend'].includes(status))
            return false
        if (userType === cs.requestor)
            return false;

        if (userType === cs.approver) {
            if (isEmpty(requestor))
                return false
            if (isNotEmpty(line_manager) || isNotEmpty(focal_point))
                return false
            if (['approved', 'held'].includes(status))
                return false
            return true;
        }
        if (userType === cs.lineManager) {
            if (statusLevel === cs.approver) {
                if (isEmpty(requestor))
                    return true
                if (['approved'].includes(status))
                    return true
            }
            if (statusLevel === cs.lineManager) {
                if (isEmpty(approver))
                    return false;
                if (['edited'].includes(status))
                    return true
            }
            if (statusLevel === cs.focalPoint) {
                if (isEmpty(approver))
                    return true;
            }
            return false
        }
        if (userType === cs.focalPoint) {
            return false
        }
    }

    function getSendMailAccess(data, userType) {
        const { status, statusLevel } = data?.currStatusObj || {};
        const { requestor, approver } = data;
        const cs = { ...constants }
        if (['mailsend'].includes(status))
            return false
        if (userType === cs.focalPoint) {
            if (statusLevel === cs.approver) {
                if (isEmpty(requestor))
                    return true
                if (['approved'].includes(status))
                    return true
            }
            if (statusLevel === cs.lineManager) {
                if (isEmpty(approver))
                    return true
                if (['approved'].includes(status))
                    return true
            }
            if (statusLevel === cs.focalPoint) {
                if (isEmpty(approver))
                    return false
            }
            return false
        }
        return false
    }

    async function handleStatusClick(status) {
        const payload = {
            status
        }
        setPayload(payload);
        setNotesMdl(true);
    };

    async function handleMdlSave({ editRemarks }) {
        setNotesMdl(false)
        setLoadingScreen(true);
        try {
            const reqBody = {
                remarks: editRemarks, ...payload
            }
            const { message } = await updateNewProdReqStatusServ(reqBody, newProdReqId);
            queryClient.invalidateQueries();
            notify("success", message);
            navigate(`${route.cNewProdreqinfo}/?newProdReqId=${newProdReqId}`, { replace: true })
        } catch (error) {
            console.log(error)
            customAlert(error)
        }
        setLoadingScreen(false);
    }

    function handleEditBtnClick() {
        navigate(route.cEditNewProdReq, { state: { newProdReqId } });
    };

    function handleAttachClick(fileData) {
        window.open(fileData?.fileUrl, "_blank", "noopener,noreferrer");
    }

    async function handleSendMail() {
        setLoadingScreen(true);
        try {
            const reqBody = {
                newProdReqIdStrg: newProdReqId
            }
            const { message } = await sendMailNewProdReqServ(reqBody);
            queryClient.invalidateQueries();
            notify("success", message);
            handlePoMsgMdlOpen()
        } catch (error) {
            console.log(error)
            customAlert(error)
        }
        setLoadingScreen(false);
    }


    if (isError) {
        customAlert(error);
        return <h2>Something went wrong</h2>
    }

    return (
        <Box height={"100%"} width={"100%"}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%", mb: 1 }}>
                <BackButton width={"20%"} />
                <Typography style={{ fontSize: 18, fontWeight: 500 }} >New Product Request Information</Typography>
                <Stack width={"20%"}>
                    {access.canEdit &&
                        <Button variant='outlined' onClick={handleEditBtnClick} sx={{
                            fontSize: 13, ml: 5,
                            color: theme?.palette?.custom?.btn, borderColor: theme?.palette?.custom?.btn, height: 30
                        }}>
                            Edit Info</Button>}
                    {ctxtUser?.isComUser && (reqData?.currStatus === "mailsend" &&
                        <Button variant="contained" sx={{
                            borderRadius: 1, backgroundColor: "rgba(255, 183, 3, 1)", fontWeight: 600, fontSize: 14,
                            color: "rgba(7, 42, 200, 1)"
                        }} >
                            Mail forwarded to AMTC team</Button>)}
                </Stack>
            </Stack>
            <BgBoxOld height={"93%"} py={1} px={1}>
                <Stack direction={"row"} height={"100%"} width={"100%"} position={"relative"}>
                    <Stack sx={{ height: "100%", width: "35%", marginLeft: 2 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>New Product Request ID: {newProdReqId}</Typography>
                        <Stack gap={0.7} mt={1}>
                            <LabelTxt typoname={"Project"} typodetail={reqData?.projName} overflow={48} namestyle={namestyle} detailstyle={detailstyle} />
                            <LabelTxt typoname={"Site Location"} typodetail={reqData?.siteLocation} overflow={48} namestyle={namestyle} detailstyle={detailstyle} />
                            <LabelTxt typoname={"Department"} typodetail={reqData?.deptName} overflow={48} namestyle={namestyle} detailstyle={detailstyle} />
                        </Stack>
                        <Typography sx={{ fontSize: 15, fontWeight: 600, mt: 3 }}>Timeline </Typography>
                        <Stack width={"100%"} height={"84%"}>
                            <ProdReqTimeline dataArr={reqData?.timeline || []} />
                        </Stack>
                    </Stack>
                    <Divider orientation="vertical" flexItem sx={{ height: "100%" }} />
                    {reqData && <Stack gap={1} mt={1} sx={{ width: "64%", px: 4 }}>
                        <CtrlLabelTxtFieldInfo readOnly={true} value={reqData?.mailSubject} name="mailSubject" title="Subject" {...txtFieldSx} />
                        <CtrlLabelTxtFieldInfo readOnly={true} value={reqData?.mailContent} name="mailContent" title="Description" {...txtFieldSx}
                            rows={11} multiline={true} height={200} />
                        {/* <AttachFileUpload title={"Attachments"} height={100} width={"100%"} filesArr={attchs} onUpload={handleOnFileUpload} onRemove={handleFileRemove} /> */}
                        <Typography style={{ color: "rgba(0, 0, 0, 0.8)", fontWeight: 500, fontSize: 12 }} >Attachments</Typography>
                        <Box sx={{ borderColor: "red", border: 1, borderRadius: 1, height: 120, color: "rgba(0,0,0,0.4)", pt: 0.5 }} >
                            <ScrollBox pr={4} height={"93%"}>
                                <Grid container
                                    direction="row"
                                    justifyContent="start"
                                    alignItems="center"
                                >
                                    {(reqData?.attachFiles || []).map((i, index) => (
                                        <Stack direction={"row"} alignItems={"center"} gap={1} key={index} justifyContent={"center"}
                                            onClick={() => handleAttachClick(i)} sx={{
                                                height: 30, width: 200, border: 1, borderColor: "rgba(0,0,0,0.5)", borderRadius: 4, mx: 1,
                                                mb: 1, bgcolor: "rgba(162, 214, 249, 0.18)",
                                                cursor: "pointer", // Indicate clickability
                                                transition: "transform 0.2s, background-color 0.2s",
                                                "&:hover": {
                                                    bgcolor: "rgba(162, 214, 249, 0.3)", // Change background color on hover
                                                },
                                                "&:active": {
                                                    transform: "scale(0.95)", // Add scale effect on click
                                                    bgcolor: "rgba(162, 214, 249, 0.5)", // Slightly darker background when clicked
                                                },
                                            }}  >
                                            <InsertDriveFile sx={{ color: "red" }} />
                                            <Stack width={140} alignItems={"center"}>
                                                <Tooltip title={i.fileName}>
                                                    <Typography sx={{ fontSize: 12, color: "rgba(0,0,0,0.8)" }}>
                                                        {makeTxtOverFLow(i.fileName, 17)}
                                                    </Typography>
                                                </Tooltip>
                                            </Stack>
                                        </Stack>

                                    ))}
                                </Grid>
                            </ScrollBox>
                        </Box>
                    </Stack>}

                    {access.canApprove && <Box sx={{ position: "absolute", bottom: 2, right: 20 }}>
                        <Button variant="outlined" onClick={async () => handleStatusClick("held")} sx={{
                            fontWeight: 600, height: 40, width: 100, borderRadius: 2, backgroundColor: theme.palette.custom.grey,
                            color: theme.palette.custom.btn, mr: 2,
                        }}>HOLD</Button>
                        <Button variant="contained" onClick={async () => handleStatusClick("approved")} sx={{
                            borderRadius: 2, backgroundColor: "rgba(255, 183, 3, 1)", width: 100, height: 40, fontWeight: 600,
                            color: "rgba(7, 42, 200, 1)"
                        }} >
                            APPROVE</Button>
                    </Box>}
                    {access.canSend && <Box sx={{ position: "absolute", bottom: 2, right: 20 }}>
                        <Button variant="contained" onClick={handleSendMail} endIcon={<Send />} sx={{
                            borderRadius: 2, backgroundColor: "rgba(255, 183, 3, 1)", height: 40, fontWeight: 600,
                            color: "rgba(7, 42, 200, 1)"
                        }} >
                            Send</Button>
                    </Box>}
                </Stack>
            </BgBoxOld>
            {payload && <NotesMdl modalOpen={notesMdl} handleModalClose={handleNotesMdlClose} handleMdlSave={handleMdlSave} payload={payload} />}
            <MessagePopUp isOpen={poMsgOpen} onClose={handlePoMsgMdlClose} message={dialogMessage} />
        </Box>
    );
}
export default NewProdReqInfoPage;